import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/SEOHead';
import { Cookie, Settings, BarChart3, Shield, ToggleRight } from 'lucide-react';

const CookiePolicyPage = () => {
  const seoConfig = {
    title: 'Cookie Policy | WEQASA',
    description: 'Informativa sui cookie utilizzati dalla piattaforma WEQASA. Scopri come gestiamo i cookie per migliorare la tua esperienza.',
    keywords: 'cookie policy weqasa, informativa cookie, gestione cookie'
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead {...seoConfig} />
      <Header />

      {/* Hero Section */}
      <section className="bg-[#F5F5F7] py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#0071e3]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Cookie className="w-8 h-8 text-[#0071e3]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4">
            Cookie Policy
          </h1>
          <p className="text-lg text-[#86868B]">
            Ultimo aggiornamento: Dicembre 2024
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduzione */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">1. Cosa sono i Cookie</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                I cookie sono piccoli file di testo che i siti web salvano sul tuo dispositivo (computer, 
                tablet, smartphone) durante la navigazione. Servono a migliorare l'esperienza di navigazione, 
                ricordare le tue preferenze e raccogliere informazioni statistiche sull'utilizzo del sito.
              </p>
              <p className="text-[#424245] leading-relaxed">
                La presente Cookie Policy spiega quali cookie utilizziamo sul sito weqasa.it e come 
                puoi gestire le tue preferenze.
              </p>
            </div>

            {/* Tipologie */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">2. Tipologie di Cookie Utilizzati</h2>
              
              {/* Cookie Tecnici */}
              <div className="bg-[#F5F5F7] rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <Settings className="w-6 h-6 text-[#0071e3]" />
                  <h3 className="text-xl font-medium text-[#1D1D1F]">Cookie Tecnici (Necessari)</h3>
                </div>
                <p className="text-[#424245] leading-relaxed mb-4">
                  Sono essenziali per il funzionamento del sito e non possono essere disattivati. 
                  Includono:
                </p>
                <ul className="space-y-2 text-[#424245]">
                  <li>• <strong>Cookie di sessione:</strong> mantengono attiva la tua sessione di navigazione</li>
                  <li>• <strong>Cookie di autenticazione:</strong> ti mantengono connesso al tuo account</li>
                  <li>• <strong>Cookie di sicurezza:</strong> proteggono da attività fraudolente</li>
                  <li>• <strong>Cookie di preferenze:</strong> ricordano la lingua e le impostazioni</li>
                </ul>
                <div className="mt-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                    Sempre attivi
                  </span>
                </div>
              </div>

              {/* Cookie Analitici */}
              <div className="bg-[#F5F5F7] rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <BarChart3 className="w-6 h-6 text-[#0071e3]" />
                  <h3 className="text-xl font-medium text-[#1D1D1F]">Cookie Analitici (Statistici)</h3>
                </div>
                <p className="text-[#424245] leading-relaxed mb-4">
                  Ci aiutano a capire come i visitatori interagiscono con il sito, raccogliendo 
                  informazioni in forma anonima. Utilizziamo:
                </p>
                <ul className="space-y-2 text-[#424245]">
                  <li>• <strong>Google Analytics 4:</strong> analisi del traffico e comportamento utenti</li>
                  <li>• <strong>Hotjar:</strong> mappe di calore e registrazioni sessioni (anonimizzate)</li>
                </ul>
                <p className="text-[#424245] leading-relaxed mt-4">
                  Questi cookie raccolgono informazioni come: pagine visitate, tempo di permanenza, 
                  dispositivo utilizzato, provenienza geografica (a livello di città/regione).
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                    Opzionali - Consenso richiesto
                  </span>
                </div>
              </div>

              {/* Cookie Marketing */}
              <div className="bg-[#F5F5F7] rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <ToggleRight className="w-6 h-6 text-[#0071e3]" />
                  <h3 className="text-xl font-medium text-[#1D1D1F]">Cookie di Marketing (Profilazione)</h3>
                </div>
                <p className="text-[#424245] leading-relaxed mb-4">
                  Sono utilizzati per mostrarti pubblicità pertinenti ai tuoi interessi. Possono essere 
                  impostati da noi o da terze parti. Includono:
                </p>
                <ul className="space-y-2 text-[#424245]">
                  <li>• <strong>Google Ads:</strong> remarketing e pubblicità personalizzata</li>
                  <li>• <strong>Facebook Pixel:</strong> tracciamento conversioni e retargeting</li>
                  <li>• <strong>LinkedIn Insight Tag:</strong> analisi pubblicitaria B2B</li>
                </ul>
                <div className="mt-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-full">
                    Opzionali - Consenso richiesto
                  </span>
                </div>
              </div>
            </div>

            {/* Tabella Cookie */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">3. Elenco Dettagliato dei Cookie</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#F5F5F7]">
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Nome Cookie</th>
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Tipo</th>
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Durata</th>
                      <th className="text-left p-4 font-semibold text-[#1D1D1F]">Finalità</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#424245]">
                    <tr className="border-b border-gray-100">
                      <td className="p-4">weqasa_session</td>
                      <td className="p-4">Tecnico</td>
                      <td className="p-4">Sessione</td>
                      <td className="p-4">Gestione sessione utente</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">weqasa_auth</td>
                      <td className="p-4">Tecnico</td>
                      <td className="p-4">30 giorni</td>
                      <td className="p-4">Autenticazione</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">weqasa_consent</td>
                      <td className="p-4">Tecnico</td>
                      <td className="p-4">12 mesi</td>
                      <td className="p-4">Preferenze cookie</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">_ga, _ga_*</td>
                      <td className="p-4">Analitico</td>
                      <td className="p-4">2 anni</td>
                      <td className="p-4">Google Analytics</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">_gid</td>
                      <td className="p-4">Analitico</td>
                      <td className="p-4">24 ore</td>
                      <td className="p-4">Google Analytics</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="p-4">_fbp</td>
                      <td className="p-4">Marketing</td>
                      <td className="p-4">3 mesi</td>
                      <td className="p-4">Facebook Pixel</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Gestione Cookie */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">4. Come Gestire i Cookie</h2>
              
              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">4.1 Banner Cookie</h3>
              <p className="text-[#424245] leading-relaxed mb-6">
                Al primo accesso al sito, ti verrà mostrato un banner che ti permette di accettare 
                o rifiutare i cookie non essenziali. Puoi modificare le tue preferenze in qualsiasi 
                momento cliccando su "Impostazioni Cookie" nel footer del sito.
              </p>

              <h3 className="text-xl font-medium text-[#1D1D1F] mb-3">4.2 Impostazioni del Browser</h3>
              <p className="text-[#424245] leading-relaxed mb-4">
                Puoi anche gestire i cookie direttamente dalle impostazioni del tuo browser:
              </p>
              <ul className="space-y-2 text-[#424245]">
                <li>• <strong>Chrome:</strong> Impostazioni → Privacy e sicurezza → Cookie</li>
                <li>• <strong>Firefox:</strong> Opzioni → Privacy e sicurezza → Cookie</li>
                <li>• <strong>Safari:</strong> Preferenze → Privacy → Gestione dati siti web</li>
                <li>• <strong>Edge:</strong> Impostazioni → Cookie e autorizzazioni sito</li>
              </ul>
            </div>

            {/* Cookie Terze Parti */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">5. Cookie di Terze Parti</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Alcuni cookie sono impostati da servizi di terze parti che appaiono sulle nostre pagine. 
                Non abbiamo controllo su questi cookie. Per maggiori informazioni, consulta le policy 
                dei rispettivi fornitori:
              </p>
              <ul className="space-y-2 text-[#424245]">
                <li>• <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] hover:underline">Google Privacy Policy</a></li>
                <li>• <a href="https://www.facebook.com/policy/cookies/" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] hover:underline">Facebook Cookie Policy</a></li>
                <li>• <a href="https://stripe.com/it/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0071e3] hover:underline">Stripe Privacy Policy</a></li>
              </ul>
            </div>

            {/* Aggiornamenti */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">6. Aggiornamenti alla Cookie Policy</h2>
              <p className="text-[#424245] leading-relaxed">
                Questa Cookie Policy può essere aggiornata periodicamente per riflettere modifiche 
                nei cookie utilizzati o per altri motivi operativi, legali o regolamentari. 
                Ti invitiamo a consultare regolarmente questa pagina per rimanere informato.
              </p>
            </div>

            {/* Contatti */}
            <div className="bg-[#F5F5F7] rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">7. Contatti</h2>
              <p className="text-[#424245] leading-relaxed mb-4">
                Per domande relative ai cookie o alla presente policy, contattaci:
              </p>
              <div className="space-y-2 text-[#424245]">
                <p><strong>WEQASA S.r.l.</strong></p>
                <p>Email: <a href="mailto:privacy@weqasa.it" className="text-[#0071e3] hover:underline">privacy@weqasa.it</a></p>
                <p>Responsabile Protezione Dati (DPO): dpo@weqasa.it</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
